import { receiveNumber, receiveString } from "../../../../controllers/adapters/receives"

/**
 * Formatea `2026-10-05` -> `05/10/2026`. Si el valor ya es texto
 * (`5 d'octubre de 2026`) se devuelve tal cual.
 */
const formatoFecha = (valor: any) => {

    const texto = receiveString(valor)

    if (!texto) return ''

    if (texto.includes(' ')) return texto

    const [anio, mes, dia] = texto.split('-')

    return anio && mes && dia ? `${dia}/${mes}/${anio}` : texto
}

/**
 * Mapea una fila del WS (prefijos `in_*`, `cli_*`, `ac_*`) a la estructura
 * interna. El listado de "Mis actividades" devuelve una fila por ACTIVIDAD de
 * la empresa (datos `ac_*` + total de inscritos + porcentaje), aunque también
 * se toleran filas de inscrito (`in_id_inscripcion` + persona).
 */
const schemaInscripcion = (item: any = {}) => {

    const nombre = receiveString(item.nombre_completo)
        || [receiveString(item.cli_nombre), receiveString(item.cli_apellidos)].filter(Boolean).join(' ').trim()

    return {
        id: receiveString(item.in_id_inscripcion || item.ac_id_actividad),
        persona: nombre,
        nif: receiveString(item.cli_nif_cif),
        actividad: receiveString(item.ac_actividad),
        actividadId: receiveString(item.ac_id_actividad),
        lugar: receiveString(item.ac_lugar_centro),
        finicio: formatoFecha(item.ac_finicio),
        ffin: formatoFecha(item.ac_ffin),
        permanente: receiveString(item.ac_espermanente) === '1',
        inscritos: receiveString(item.total_inscritos_actividad),
        porcentaje: receiveString(item.porcentaje),
        asistencia: receiveString(item.porcentaje) || receiveString(item.in_porcentaje) || receiveString(item.asistencia),
        encuesta: receiveString(item.ac_linkencuesta),
        estado: receiveString(item.estado) || receiveString(item.in_estado),
        in_estado: receiveString(item.in_estado),
        precio: receiveString(item.in_precio),
    }
}

/**
 * Extrae el listado de la respuesta del WS. Una fila es válida si trae
 * `ac_id_actividad` (fila de actividad de "Mis actividades") o
 * `in_id_inscripcion` (fila de inscrito).
 */
const extraerLista = (data: any): any[] => {

    if (!data) return []

    const esFila = (item: any) => item && typeof item === 'object' && (item.ac_id_actividad || item.in_id_inscripcion)

    // { data: { data: [...], total, ... } }
    if (data?.data && typeof data.data === 'object' && !Array.isArray(data.data) && Array.isArray(data.data?.data)) {

        return data.data.data.filter(esFila)
    }

    // { data: [...] }
    if (Array.isArray(data?.data)) return data.data.filter(esFila)

    if (Array.isArray(data)) return data.filter(esFila)

    return []
}

const InscripcionesAdapters = {

    // GET {base}/integraWeb.php?accion=listar_inscripciones&empresa_id=...&cif=...&actividad=...&page=N
    // Listado de ACTIVIDADES asignadas a la empresa (una fila por actividad con
    // el total de inscritos), con paginación en el objeto `data` (total, per_page,
    // current_page, last_page), igual que en cursos y actividades. `empresa_id`
    // identifica la empresa del backoffice y `cif` el NIF/CIF (igual que en
    // inscribir_personas_actividad); `actividad` filtra por nombre.
    LISTAR_INSCRIPCIONES: {

        send: {

            schema: function (item: any = {}) {

                return {
                    accion: item.accion || 'listar_inscripciones',
                    empresa_id: item.empresa_id ?? item.empresaId ?? '',
                    cif: item.cif || '',
                    actividad: item.actividad || '',
                    page: item.page || 1,
                    ...item,
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: schemaInscripcion,

            adapter: function (data: any) {

                const items = extraerLista(data).map((item: any) => this.schema(item))

                const paginacion = data?.data || data || {}

                return {
                    items,
                    paginacion: {
                        total: receiveNumber(paginacion.total),
                        per_page: receiveNumber(paginacion.per_page),
                        page: receiveNumber(paginacion.current_page),
                        last_page: receiveNumber(paginacion.last_page),
                    }
                }
            }
        },
    },

    // GET {base}/integraWeb.php?accion=listar_inscripciones_personas&empresa_id=...&cif=...&actividad_id=...&estado=...&page=N
    // Inscritos (personas) de UNA actividad concreta, con paginación en el objeto
    // `data`. `estado` filtra por código de estado de inscripción (ver
    // `estados_incripcion`) y `''` devuelve todos.
    LISTAR_INSCRIPCIONES_PERSONAS: {

        send: {

            schema: function (item: any = {}) {

                return {
                    accion: item.accion || 'listar_inscripciones_personas',
                    empresa_id: item.empresa_id ?? item.empresaId ?? '',
                    cif: item.cif || '',
                    actividad_id: item.actividad_id ?? item.ac_id_actividad ?? '',
                    estado: item.estado || '',
                    page: item.page || 1,
                    ...item,
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: schemaInscripcion,

            adapter: function (data: any) {

                const items = extraerLista(data).map((item: any) => this.schema(item))

                const paginacion = data?.data || data || {}

                return {
                    items,
                    paginacion: {
                        total: receiveNumber(paginacion.total),
                        per_page: receiveNumber(paginacion.per_page),
                        page: receiveNumber(paginacion.current_page),
                        last_page: receiveNumber(paginacion.last_page),
                    }
                }
            }
        },
    },

    // GET {base}/integraWeb.php?accion=estados_incripcion
    // Catálogo de estados de inscripción: el WS devuelve un objeto `id -> etiqueta`
    // que se normaliza a un array de opciones [{ value, label }] para el filtro.
    GET_ESTADOS_INSCRIPCION: {

        send: {

            schema: function (item: any = {}) {

                return {
                    accion: item.accion || 'estados_incripcion',
                    ...item,
                }
            },

            adapter: function (data: any) {

                const item = this.schema(data)

                return {
                    ...item
                }
            }
        },

        receive: {

            schema: function (item: any = {}) {

                return {
                    estados: item && typeof item === 'object' && !Array.isArray(item)
                        ? Object.entries(item).map(([value, label]: [string, any]) => ({
                            value: receiveString(value),
                            label: receiveString(label),
                        }))
                        : [],
                }
            },

            adapter: function (data: any) {

                return this.schema(data?.data ?? data)
            }
        },
    },
}

export {
    InscripcionesAdapters
}